/** Port of mobile/lib/ui/pixel/pixel_action_button.dart. */

import type { ReactNode } from 'react';

import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from './PixelContainer';
import { PixelLoadingDots } from './PixelLoadingDots';

export type PixelActionButtonVariant = 'filled' | 'tonal';

const contentHeight = 22;

/** Labeled pixel button with fixed height and optional busy dots. */
export function PixelActionButton({
  label,
  onClick,
  busy = false,
  variant = 'filled',
  leading,
  fontSize = 22,
  className = '',
}: {
  label: string;
  onClick?: () => void;
  busy?: boolean;
  variant?: PixelActionButtonVariant;
  leading?: ReactNode;
  fontSize?: number;
  className?: string;
}) {
  const colors = useThemeColors();
  const filled = variant === 'filled';
  const background = filled ? colors.onSurface : colors.surfaceContainerHighest;
  const foreground = filled ? colors.surface : colors.onSurface;
  const padding = filled ? '12px 0' : '14px 16px';

  return (
    <PixelContainer
      radius={3}
      pixelScale={2}
      padding={padding}
      backgroundColor={background}
      borderColor={colors.onSurface}
      onClick={busy ? undefined : onClick}
      className={`w-full ${className}`}
    >
      <div className="flex items-center justify-center" style={{ height: contentHeight }}>
        {busy ? (
          <PixelLoadingDots color={foreground} dotSize={5} gap={6} />
        ) : (
          <div className="flex items-center justify-center gap-2.5">
            {leading}
            <span className="leading-none" style={{ fontSize, color: foreground }}>
              {label}
            </span>
          </div>
        )}
      </div>
    </PixelContainer>
  );
}

/** Understated text action with the same busy dots. */
export function PixelLinkButton({
  label,
  onClick,
  busy = false,
  disabled = false,
}: {
  label: string;
  onClick?: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  const colors = useThemeColors();
  const color = colors.onSurface;

  return (
    <button
      type="button"
      className="mt-4 flex h-5 w-full items-center justify-center leading-none underline disabled:opacity-50"
      style={{ fontSize: 14, color, opacity: 0.7 }}
      disabled={busy || disabled}
      onClick={onClick}
    >
      {busy ? <PixelLoadingDots color={color} dotSize={5} gap={6} /> : label}
    </button>
  );
}
